/**
 * eTMF completeness — score thresholds, colours and labels (ADR-0036).
 *
 * The thresholds mirror the backend's `completeness_check.RAG_*` constants
 * (Green ≥ 90, Amber 60–89, Red < 60); the reference-model endpoint returns
 * them so the two cannot drift silently.
 */

import type { ColumnScore, CompletenessRow } from "@/shared/api/etmfCompleteness";

export const DEFAULT_GREEN_MIN = 90;
export const DEFAULT_AMBER_MIN = 60;

export type ColumnKey = "existence" | "tmf" | "sop" | "protocol" | "quality";

export const COLUMNS: { key: ColumnKey; label: string; hint: string }[] = [
  {
    key: "existence",
    label: "Existence",
    hint: "Present in Veeva + required metadata + final status (no AI)",
  },
  { key: "tmf", label: "vs TMF", hint: "Contains what the DIA TMF artifact requires" },
  { key: "sop", label: "vs SOP", hint: "Meets the uploaded SOP expectations" },
  {
    key: "protocol",
    label: "vs Protocol",
    hint: "Consistent with the Protocol (uploaded, else filed in Veeva)",
  },
  { key: "quality", label: "Quality", hint: "ALCOA+ scorecard aggregated to 0–100" },
];

export type Rag = "green" | "amber" | "red" | "none";

export function ragOf(
  score: number | null | undefined,
  green = DEFAULT_GREEN_MIN,
  amber = DEFAULT_AMBER_MIN,
): Rag {
  if (score === null || score === undefined) return "none";
  if (score >= green) return "green";
  if (score >= amber) return "amber";
  return "red";
}

export const RAG_COLOR: Record<Rag, string> = {
  green: "teal",
  amber: "yellow",
  red: "red",
  none: "gray",
};

export const RAG_LABEL: Record<Rag, string> = {
  green: "Green",
  amber: "Amber",
  red: "Red",
  none: "Not scored",
};

/** What a cell shows when there is no number. */
export function scoreText(c: ColumnScore): string {
  if (c.score !== null && c.score !== undefined) return String(c.score);
  return VERDICT_LABEL[c.verdict] ?? "—";
}

export const VERDICT_LABEL: Record<string, string> = {
  pass: "Pass",
  partial: "Partial",
  fail: "Fail",
  missing: "Missing",
  not_run: "—",
  not_assessable: "N/A",
  not_applicable: "N/A",
  unreadable: "Unreadable",
  error: "Error",
};

export const VERDICT_COLOR: Record<string, string> = {
  pass: "teal",
  partial: "yellow",
  fail: "red",
  missing: "red",
  error: "red",
  unreadable: "orange",
  not_applicable: "gray",
  not_assessable: "gray",
  not_run: "gray",
};

/** Missing, any column below amber, misfiled, or an AI error. */
export function needsAttention(row: CompletenessRow, amber = DEFAULT_AMBER_MIN): boolean {
  if (isMissing(row) || row.misfiled) return true;
  return COLUMNS.some((c) => {
    const col = row[c.key];
    return col?.verdict === "error" || (col?.score != null && col.score < amber);
  });
}

export function isMissing(row: CompletenessRow): boolean {
  return !row.document_id;
}

export function formatUtcShort(value: string | null | undefined): string {
  if (!value) return "Never";
  const d = new Date(value);
  return `${d.toISOString().slice(0, 16).replace("T", " ")} UTC`;
}
